import React, { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { TEAM_TYPE } from '../types/Team';
import get from 'lodash/get';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { Link, useLocation } from 'react-router-dom';
import CompareIcon from '../icons/CompareIcon';
import NamedBox from '../atoms/NamedBox';
import Loader from '../atoms/Loader';
import { getScoreValue } from '../atoms/Score';
import TopPlayerCard from '../molecules/TopPlayerCard';
import gql from '../services/gql';
import { getHighSchoolAccessYears } from '../services/user';
import {
  COLOR_BLUE,
  COLOR_DARK_GRAY,
} from '../styles/colors';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';
import ComparePlayer, { ComparePlayerType } from '../types/ComparePlayer';
import Player from '../types/Player';
import User from '../types/User';
import HighSchool from '../types/HighSchool';
import Position from '../types/Position';

interface PlayerProfileTopPlayersProps {
  className?: string;
  currentPlayer: Player;
  user?: User;
  selectedPosition?: Position;
  selectedTeamType?: string;
  hideCombinePlayersLink?: boolean;
  printed?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  playerProfileTopPlayers: {
    display: 'flex',
    width: '100%',
    border: 0,
    boxShadow: 'none',
  },
  printed: {
    '& $playerCards': {
      overflowX: 'auto',
      paddingBottom: 0,
    },
  },

  headerClassName: {
    alignItems: 'stretch',
    justifyContent: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
  },
  headerName: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    fontWeight: 700,
  },
  
  compareWrapper: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
  },
  compareIcon: {
    flexShrink: 0,
    width: '18px',
    height: '18px',
    color: COLOR_BLUE,
  },
  compareLink: {
    marginLeft: theme.spacing(1),
    fontFamily: FONT_PROXIMA_NOVA,
    lineHeight: 1.1,
    fontWeight: 700,
    color: COLOR_BLUE,

    [theme.breakpoints.down('xs')]: {
      fontSize: theme.typography.pxToRem(14),
    },
  },

  playerCardsWrapper: {
    width: '100%',
    position: 'relative',

    '&:after': {
      content: '""',
      width: '100px',
      height: '100%',
      position: 'absolute',
      right: 0,
      top: 0,
      background: 'linear-gradient(to right, rgba(255,255,255,0), rgba(255,255,255,1))',
    },
  },
  playerCards: {
    position: 'relative',
    display: 'flex',
    overflowX: 'scroll',
    padding: theme.spacing(3, 0),
  },
  noData: {
    color: COLOR_DARK_GRAY,
    margin: '0 auto',
    padding: theme.spacing(1, 0, 2),
  },
  playerCard: {
    flexGrow: 1,
    marginLeft: theme.spacing(3),
    boxShadow: '0 12px 12px rgba(0, 0, 0, 0.05)',

    '&:first-of-type': {
      marginLeft: 0,
    },
  },
  space: {
    width: '2px',
    background: 'none',
    flexShrink: 0,
  },
}), { name: PlayerProfileTopPlayers.name });

export default function PlayerProfileTopPlayers (props: PlayerProfileTopPlayersProps) {
  const {
    className,
    currentPlayer,
    user,
    selectedPosition,
    hideCombinePlayersLink = false,
    printed = false,
    selectedTeamType
  } = props;
  const classes = useStyles();
  const location = useLocation();
  const newProfileEnabled = location.pathname.includes('player-profile/');
  const [topPlayers, setTopPlayers] = useState<ComparePlayer[]>([]);
  const [comparePlayerType, setComparePlayerType] = useState<string | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const fetchRequestIdRef = useRef<number>(0);
  const FETCH_STABILIZE_MS = 120;
  const hasSelectedPositionOverride = !!selectedPosition;

  const effectiveSelectedPosition = useMemo(() => {
    const resolvePositions = () => {
      if (selectedTeamType === TEAM_TYPE.NFL) {
        const primaryNFLTeam = (currentPlayer?.playerNFLTeams || [])
          .find(nflTeam => nflTeam && nflTeam.isPrimary);
        return (primaryNFLTeam?.positions || []).filter(position => !!position?.code);
      }

      if (selectedTeamType === TEAM_TYPE.COLLEGE) {
        const primaryCollege = (currentPlayer?.playerColleges || [])
          .find(college => college && college.isPrimary);
        return (primaryCollege?.positions || []).filter(position => !!position?.code);
      }

      const primaryHighSchool = (currentPlayer?.highSchools || [])
        .find((highSchool:HighSchool) => highSchool && highSchool.isPrimary);
      return (primaryHighSchool?.positions || []).filter(position => !!position?.code);
    };

    if (selectedPosition) {
      const positions = resolvePositions();
      if (!positions.length) {
        return selectedPosition;
      }
  
      const hasSelectedPosition = !!selectedPosition
        && positions.some(position => position.code === selectedPosition);
      if (hasSelectedPosition) {
        return selectedPosition;
      }
  
      return positions.find(position => position.isPrimary)?.code || positions[0]?.code;
    }

  }, [currentPlayer, selectedPosition, selectedTeamType]);



  useEffect(() => {
    if (!currentPlayer || !user?.accessLevel || selectedTeamType === '') {
      return;
    }

    let comparePlayerType = TEAM_TYPE.HIGH_SCHOOL;
    if (selectedTeamType) {
      comparePlayerType = selectedTeamType;
    } else {
      if (currentPlayer?.playerNFLTeams?.length) {
        comparePlayerType = TEAM_TYPE.NFL;
      } else if (currentPlayer?.playerColleges?.length) {
        comparePlayerType = TEAM_TYPE.COLLEGE;
      }
    }

    setComparePlayerType(comparePlayerType);
    const timerId = window.setTimeout(() => {
      fetchTopPlayers(
        comparePlayerType,
        hasSelectedPositionOverride ? effectiveSelectedPosition : undefined,
      );
    }, FETCH_STABILIZE_MS);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [user, currentPlayer, selectedTeamType, effectiveSelectedPosition]);

  function fetchTopPlayers (comparePlayerType:string, positionOverride?: Position) {
    fetchRequestIdRef.current += 1;
    const currentRequestId = fetchRequestIdRef.current;
    setLoading(true);

    const currentYear:number = new Date().getFullYear();

    const accessYearMapping:any = {
      'senior': currentYear,
      'junior': currentYear + 1,
      'sophomore': currentYear + 2,
      'freshman': currentYear + 3,
    };

    const highSchoolAccessYears = getHighSchoolAccessYears(user);

    const highSchoolGradYearTo:number = highSchoolAccessYears.length > 0
      ? accessYearMapping[highSchoolAccessYears[0]] || 0
      : 0;
    const highSchoolGradYearFrom:number = highSchoolAccessYears.length > 0
      ? accessYearMapping[highSchoolAccessYears[highSchoolAccessYears.length - 1]] || 0
      : 0;

    const hasPAIData = currentPlayer && typeof getScoreValue(currentPlayer.pai) === 'number';
    const hasCombineData = currentPlayer && typeof getScoreValue(currentPlayer.combine) === 'number';
    const primaryHS:HighSchool = (currentPlayer.highSchools || []).find((highSchool:HighSchool) => highSchool && highSchool.isPrimary) || {} as HighSchool;
    const hasMultiSportData = currentPlayer && !!currentPlayer?.multiSport.length;

    const stars = (typeof currentPlayer?._247Star === 'number' || typeof currentPlayer?.compStar === 'number')
      ? currentPlayer?._247Star || currentPlayer?.compStar || 0
      : null;

    const comparePlayerTypeString = comparePlayerType === TEAM_TYPE.HIGH_SCHOOL ? ComparePlayerType.HIGH_SCHOOL : comparePlayerType;
    const positionQuery = positionOverride
      ? `position: "${positionOverride}",`
      : '';

    gql(`
      comparePlayersCached(
        sourcePlayerSlug: "${currentPlayer.slug}",
        ${positionQuery}
        resultType: "${comparePlayerTypeString}",
        includePai: ${!!hasPAIData},
        includeCombine: ${!!hasCombineData},
        includeStarRating: ${typeof stars === 'number'},
        includeMultiSport: ${hasMultiSportData},
        highSchoolGradYearFrom: ${highSchoolGradYearFrom},
        highSchoolGradYearTo: ${highSchoolGradYearTo},
        includeHighSchoolArchive: false,
        highSchoolStates: [],
        includeAllYears: true,
        nflDraftYearFrom: 0,
        nflDraftYearTo: 0,
        groups: [],
        transferStatus: [],
        transferYears: [],
        conferenceIds: [],
        teamIds: [],
        limit: 5,
        includeSourcePlayer: false,
        trackPageView: false
      ) {
          id
          slug
          firstName
          lastName
          photoUrl
          percentMatch
          highSchools
          _247Star
          compStar
          pai
          ppi
          combine
          nflCombineScore
          multiSport
          bestHighSchoolMPH {
            id
            playerId
            mph
            mphPercentage
            timeToMax
            distanceTraveled
            type
            isBest
            confidencePercent
            filmUrl
            year
          }
          bestCollegeMPH {
            id
            playerId
            mph
            mphPercentile
            distanceTraveled
            filmUrl
            year
            averageTop5MPH
            ncaaMPHRank
            ncaaMPHPercentile
            mphPositionRank
            mphPosition
            timesExceeded20
          }
          hsCombines {
            id
          }
          nflCombines {
            id
            combineDate
            height
            weight
            wingspan
            arm
            hand
          }
          playerColleges {
            id
            recruitingClass
            team {
              id
              name
            }
            positions
            isPrimary
          }
          playerNFLTeams {
            id
            draftYear
            team {
              id
              name
            }
            positions
            isPrimary
          }
        }
    `)
      .then((data:any) => data.comparePlayersCached as ComparePlayer[])
      .then((comparePlayers:ComparePlayer[]) => {
        if (currentRequestId !== fetchRequestIdRef.current) {
          return;
        }

        setTopPlayers(comparePlayers && comparePlayers.length
          ? comparePlayers
          : []
        );
      })
      .catch(console.error)
      .finally(() => {
        if (currentRequestId === fetchRequestIdRef.current) {
          setLoading(false);
        }
      })
  }

  const canComparePlayers = user?.accessLevel?.canComparePlayers && !hideCombinePlayersLink;
  function getTitle() {
    if (newProfileEnabled) {
      switch (comparePlayerType) {
        case TEAM_TYPE.HIGH_SCHOOL:
          return 'High School Player Comps';
        case TEAM_TYPE.COLLEGE:
          return 'College Player Comps';
        case TEAM_TYPE.NFL:
          return 'Pro Player Comps';
      }
    }
    return 'Top Player Comparisons'
  }
  return (
    <NamedBox
      className={clsx(
        classes.playerProfileTopPlayers,
        printed && classes.printed,
        className,
      )}
      name={getTitle()}
      headerNameClassName={classes.headerName}
      headerClassName={classes.headerClassName}
      headerContent={canComparePlayers
        ? <div className={classes.compareWrapper}>
            <CompareIcon className={classes.compareIcon} />
            <Link
              className={classes.compareLink}
              to={`/compare/${currentPlayer.slug}`}
            >
              Compare Players
            </Link>
          </div>
        : null
      }
    >
      <div className={classes.playerCards}>
        <Loader inProgress={loading} />

        {!topPlayers?.length && (
          <div className={classes.noData}>
            No Top Players data
          </div>
        )}

        {topPlayers.map((player:ComparePlayer, index:number) => (
          <TopPlayerCard
            key={player.id}
            index={index}
            className={classes.playerCard}
            player={player}
            comparePlayerType={comparePlayerType}
            newProfileEnabled={newProfileEnabled}
          />
        ))}

        <div className={classes.space} />
      </div>
    </NamedBox>
  );
}

